// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { _MatTabGroupBase } from '@angular/material/tabs';

// SyncFusion
import { GridComponent, ColumnModel, SearchSettingsModel, EditMode, NewRowPosition, EditSettings, EditSettingsModel } from '@syncfusion/ej2-angular-grids';

// App
import { FieldFormatType, FieldMetaForeignKey, FieldMetadata, ViewMetadata } from '../cer-data/cer-data-types';
import { GridDesign } from './cer-grid-design';
import { CerDataMetaService } from '@data/cer-data-meta.service';
import { CerGridCommandService } from './cer-grid-command.service';
import { CerGridSelectionService } from './cer-grid-selection.service';
import { CerGridFilterService } from './cer-grid-filter.service';
import { CerGridEditCellService } from './cer-grid-edit-cell.service';
import { CerDataService } from '@data/cer-data.service';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { uiCmdColumnGroup, uiCmdColumnHide, uiCmdColumnsChoose, uiCmdDebugModeToogle, uiCmdPagingToggle, uiCmdResearch } from '@ui/cer-ui-commands';
import { CerGridEditMode } from './cer-grid.component';

// Meta sort settings
export interface MetaSortField {
  priority: number,
  field: string,
  assending: boolean
}

@Injectable()
export class CerGridSetupService implements OnDestroy {

  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid
  public grid: GridComponent;
  public design: GridDesign;

  // Columns
  public columnsIsSetup: boolean = false;
  public groupByColumnCount: number = 0;

  // Settings
  public gridColumns: ColumnModel[] = [];
  public gridSortMetaSettings: MetaSortField[];

  public debugMode: boolean = false;

  // Constructor
  constructor(public dataService: CerDataService,
    private editCell: CerGridEditCellService,
    private filter: CerGridFilterService,
    private selection: CerGridSelectionService,
    private command: CerGridCommandService,
    private dataMeta: CerDataMetaService,
    private snackBar: MatSnackBar) {

    this.subscriptionManager.add(
      this.command.command$.subscribe(event => this.onCommand(event))
    );
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }


  private initGridEditSettings() : EditSettingsModel {
    const editSettings: EditSettingsModel = {
      allowEditing: true,
      allowAdding: true,
      allowDeleting: true
    };

    const editDesign = this.design?.editSettings;
    editSettings.mode = CerGridSetupService.editModeGet(editDesign?.mode);
    editSettings.allowEditOnDblClick = editDesign?.editOnDoubleClick ?? true;
    editSettings.showConfirmDialog = editDesign?.editConfirm == null &&  editDesign?.editBatchCancelConfirm == null ? true : editDesign?.editConfirm === true || editDesign.editBatchCancelConfirm === true; 
    editSettings.showDeleteConfirmDialog = false; // handled by cer edit service;
    editSettings.newRowPosition = editDesign?.newRowPosition ?? 'Bottom';
    editSettings.dialog = { params: {} } /*, allowNextRowEdit: true */;

    const eDlg = editDesign?.dialog;
    if (eDlg) {
      if (eDlg.editInPane === true) {
        editSettings.dialog ??= {};
        editSettings.dialog.params ?? {};
        editSettings.dialog.params.target = "containerDiv";
        editSettings.dialog.params.enableResize = false;
      }
      if (eDlg.width) {
        editSettings.dialog ??= {};
        editSettings.dialog.params ?? {};
        editSettings.dialog.params.width = eDlg.width;
      }
      if (eDlg.height) {
        editSettings.dialog ??= {};
        editSettings.dialog.params ?? {};
        editSettings.dialog.params.height = eDlg.height;
        //this.grid.editSettings.dialog.params.minHeight = viewMetadata.dialogHeight;
      }
    }

    var viewMetadata = this.dataMeta?.viewMetadata;
    if (viewMetadata) {
      // Editing
      if (viewMetadata.allowEdit != undefined) {
        editSettings.allowEditing = viewMetadata.allowEdit;
      }
      // Adding
      if (viewMetadata.allowCreate != undefined) {
        editSettings.allowAdding = viewMetadata.allowCreate;
      }
      // Delete
      if (viewMetadata.allowDelete != undefined) {
        editSettings.allowDeleting = viewMetadata.allowDelete;
      }
    }
    return editSettings;
  }

  public init(g: GridComponent, d: GridDesign) {

    this.grid = g;
    this.design = d;

    //get metadata from AppData (primary idx) etc
    //g.dataSource = this.appDataService.dataManager;
    g.allowPaging = d.allowPaging;
    g.enableVirtualization = d.pagingVirtual;
    g.enableAdaptiveUI = d.card;
    g.rowRenderingMode = d.card ? 'Vertical' : 'Horizontal';
    g.allowResizing = true;
    //g.autoFit = true;
    g.allowReordering = true;
    g.allowSorting = !d.card && !(this.dataMeta.viewMetadata?.allowSorting === false);
    g.allowFiltering = !d.card && !(this.dataMeta.viewMetadata?.allowFiltering === false);
    g.allowGrouping = true; // d.toolbarType == 'default';
    g.allowExcelExport = true;
    g.showColumnChooser = true;
    g.sortSettings = {};
    //this.gridGroupSettings.enableLazyLoading = this.design.groupingLazyLoading;
    g.groupSettings = {
      disablePageWiseAggregates: true, showDropArea: false, showUngroupButton: true, showGroupedColumn: false
      /*, enableLazyLoading: true*/
    };
    //this.grid.enableAdaptiveUI = true;
    //this.grid.rowRenderingMode = 'Horizontal';
    //this.grid.rowRenderingMode = 'Vertical';

    this.grid.enableVirtualMaskRow = false;

    // Editing
    this.grid.editSettings = this.initGridEditSettings();

    // Multi select
    if (this.design.selectionMode == 'checkboxMulti' || this.design.selectionMode == 'checkboxSingle') {
      var c: ColumnModel = { type: "checkbox", width: "30px", maxWidth: "30px", headerTextAlign: "Center", textAlign: "Center" };
      this.gridColumns.unshift(c);
    }

    // Setup columns
    this.initColumnsSetup();
    
    if (d.card) {
      g.cssClass = 'e-bigger';
      d.autoFocus = (d.dataChildrenExists === true);
      d.toolbarTitle = 'metadata';
      this.gridColumns.filter(c => c.textAlign == 'Right' || c.textAlign == 'Center').forEach(c => c.textAlign = 'Left');
    }
    this.grid.columns = this.gridColumns;
  }

  static newRowPositionGet(newRowPosition: any): NewRowPosition {
    return 'Bottom';
  }

  static editModeGet(mode: CerGridEditMode): EditMode {
    switch (mode) {
      case 'dialog':
        return 'Dialog';
      case 'inline':
        return 'Normal';
      case 'inlineBatch':
        return 'Batch';
      default:
        return 'Dialog';
    }
  }

  public onCommand(event: UiCommandEvent) {
    if (event.cancel) {
      return;
    }
    var done = false;
    switch (event.source) {
      case UiCommandSourceEnum.ActionBegin:
        switch (event.actionType) {
          case UiActionTypeEnum.Created:
            break;
          case UiActionTypeEnum.DataBound:
            this.onDataBoundBegin(event.args);
            break;
        }
        break;
      case UiCommandSourceEnum.ActionComplete: {
        switch (event.actionType) {
          case UiActionTypeEnum.DataBound:
            this.onDataBoundComplete(event.args);
            break;
          case UiActionTypeEnum.DataLoad:
          case UiActionTypeEnum.DataFKLoad:
            if (!this.design.dataChild) {

              //      if (this.dataManager?.dataSource != null) {
              //        this.dataManager.dataSource.offline = false;
              //      }

              this.command.dataSourceSetOffline(false);
              this.command.refreshColumns();
            }
        }
        break;
      }
    }

    switch (event.commandId) {
      case uiCmdColumnHide.id:
        this.doColumnHide(event.columnName);
        break;
      case uiCmdColumnsChoose.id:
        this.doColumnsChoose();
        break;
      case uiCmdColumnGroup.id:
        this.doColumnGroup(event.columnName);
        break;
      case uiCmdResearch.id:
        this.command.refresh();
        break;
      case uiCmdPagingToggle.id:
        this.doPagingToggle();
        break;
      case uiCmdDebugModeToogle.id:
        done = this.doDebugModeToogle();
        break;
    }

    if (done) {
      event.cancel = true;
      event.args.cancel = true;
    }
  }

  private onDataBoundBegin(args: any) {
    this.onDataBoundUpdateFKFromNavigation(args);
  }

  private onDataBoundComplete(event: any) {
    //this.columnsSetup(); // If no field meta data
    if (event.args) {
      this.columnsGroupBySetup(event.args);
    }
  }

  private onDataBoundUpdateFKFromNavigation(args: any) {
    var data = args.result /* query+insert */ ?? ((args.data != null) ? [args.data] : null)/* update */;
    if (data?.length > 0) {
      FieldMetaForeignKey.dataMergeFromNavigationFields(this.dataMeta.fieldMetadata, data);
    }
  }

  public gridRedraw(data: Object[] = null) {
    if (!data) {
      data = this.grid.currentViewData;
    }
    this.grid.renderModule.dataManagerSuccess({ result: data, count: this.grid.pageSettings.totalRecordsCount })
  }

  // Columns data
  public refreshColumns() {
    if (this.grid && this.grid.headerModule) {
      //console.log("Keep before refresh " + this.selection.rowSelectedKeepIdx);
      this.grid.refreshColumns();
    }
  }

  public dataRowReplace(origDto: object, newDto: object, relativeSelectionIdx: number = null) {
    Object.assign(origDto, newDto);
    this.refreshPage(relativeSelectionIdx);
  }

  public refreshPage(relativeSelectionIdx: number = null) {
    this.selection.rowSelectedSave(null, relativeSelectionIdx);
    this.refreshColumns();
  }

  // Command handlers
  public doColumnHide(columnName: string) {
    if (columnName) {
      this.grid.hideColumns(columnName, 'field');
    }
  }

  public doColumnsChoose() {
    this.grid.openColumnChooser(0, 0);
  }

  public doColumnGroup(columnName: string) {
    if (columnName) {
      if (!this.grid.groupSettings.columns.includes(columnName)) {
        this.grid.groupColumn(columnName);
      }
    }
  }

  public doPagingToggle() {
    if (this.grid && false) {
      this.selection.rowSelectedSave();
      if (this.grid.allowPaging) {
        if (this.grid.enableVirtualization) {
          this.grid.enableVirtualization = false;
        }
        else {
          this.grid.allowPaging = !this.grid.allowPaging;
          this.grid.height = "100%";
        }
      }
      else {
        this.grid.allowPaging = !this.grid.allowPaging;
        if (this.design.pagingVirtual) {
          this.grid.enableVirtualization = true;
        }
      }
      this.grid.refresh();
    }
  }

  public doDebugModeToogle(): boolean {
    var ok: boolean = false;
    this.debugMode = !this.debugMode;
    this.dataService.setDebugMode(this.debugMode);
    //this.ui.snack(this.design.dataRelationRole + ' grid: Debug mode ' + this.debugMode);
    console.log(this.design.dataParent + ' ' + this.design.dataChild + ' grid: Debug mode ' + this.debugMode);
    ok = true;
    return ok;
  }

  public debug(txt: string) {
    if (this.debugMode) {
      console.log(this.design.dataParent + ' ' + this.design.dataChild + ' grid: ' + txt);
    }
  }

  // Column setup
  public initColumnsSetup() {
    if (this.dataService && this.dataService.fieldMetadata) {
      if (!this.columnsIsSetup) {
        this.columnsIsSetup = true;

        this.searchSettingsInit();

        this.dataService.fieldMetadata.forEach(m => this.columnCreateByMeta(m, this.dataService.viewMetadata));
        this.columnSortSetup();
      }
    }
  }

  /*
  public columnSetup(column: ColumnModel) {
    if (column.field) {
      var meta: FieldMetadata = this.dataService.fieldMetadata.find(f => f.name == column.field);
      if (meta) {
        this.columnSetupByMeta(column, meta);
      }
    }
  }*/

  public columnCreateByMeta(meta: FieldMetadata, viewMetadata: ViewMetadata) {
    var column: ColumnModel = {
      field: meta.name,
      headerText: meta.text
    };

    column.visible = (meta.visible !== false);

    // Data
    if (viewMetadata?.primaryKey) {
      if (Array.isArray(viewMetadata.primaryKey)) {
        viewMetadata.primaryKey.forEach(pk => {
          if (pk == meta.name) {
            column.isPrimaryKey = true;
          }
        });
      }
      else {
        if (viewMetadata.primaryKey == meta.name) {
          column.isPrimaryKey = true;
        }
      }
    }

    this.columnSetupByMeta(column, meta);

    this.gridColumns.push(column);
  }

  public field(fieldName: string): ColumnModel {
    return this.gridColumns.find(c => c.field == fieldName);
  }

  public columnSetupByMeta(column: ColumnModel, meta: FieldMetadata) {
    // Edit cell
    this.editCell.columnSetupByMeta(column, meta);

    // Order by
    if (meta.orderBy != undefined && meta.orderBy != 'None') {
      this.columnSortColumnsByMeta(meta, column);
    }
    else if (meta.allowSorting == false) {
      column.allowSorting = false;
    }

    // Group by
    if (meta.groupBy === true) {
      this.columnGroupByFieldName(column.field);
    }

    // Filter
    if (meta.allowFiltering == false) {
      column.allowFiltering = false;
    }
    else {
      //column.filter ??= {};
      //column.filter.hideSearchbox = (meta.allowFilteringSearchBox == false);
    }
    this.filter.filterColumnSetOnClient(column, meta);

    // Search
    this.columSearchSetup(meta, column);

    // Column chooser
    if (meta.allowColumnChooser === false) {
      column.showInColumnChooser = false;
    }
  }


  // Order by
  private columnSortColumnsByMeta(meta: FieldMetadata, column: ColumnModel) {
    //if (meta.visible !== false) {
    var priority: number = meta.orderByPriority ?? 1;
    var assending: boolean = (meta.orderBy === 'Ascending');
    var sortField: MetaSortField = { priority: priority, field: column.field, assending: assending };
    if (this.gridSortMetaSettings == null) {
      this.gridSortMetaSettings = [sortField];
    }
    else {
      this.gridSortMetaSettings.push(sortField);
    }
    //}
  }

  private searchSettingsInit() {
    var fields: string[] = [];
    var searchSettings: SearchSettingsModel = { operator: 'contains', fields: fields, ignoreCase: true, ignoreAccent: false };
    this.grid.searchSettings = searchSettings;
  }

  private columSearchSetup(meta: FieldMetadata, column: ColumnModel) {
    if (meta.allowSearching !== false && meta.visible !== false) {
      if (meta.format === undefined || this.columnSearchContainsAllowed(meta.format)) {
        this.grid.searchSettings.fields.push(column.field);
      }
    }
  }

  private fieldFormatCanSearchContains: FieldFormatType[] = ['Email', 'Phone', 'Text', 'TextArea', 'TextRight'];
  private columnSearchContainsAllowed(format: FieldFormatType | FieldFormatType[]): boolean {
    var canSearch: boolean = true;
    if (Array.isArray(format)) {
      format.forEach(f => {
        if (!this.columnSearchContainsAllowed(f)) {
          canSearch = false;
        }
      });
    }
    else {
      canSearch = this.fieldFormatCanSearchContains.includes(format as FieldFormatType);
    }
    return canSearch;
  }

  private columnSortSetup() {
    if (this.gridSortMetaSettings != null) {
      var columns: object[] = [];
      this.gridSortMetaSettings.sort((a, b) => a.priority - b.priority).forEach(f => {
        var column = { field: f.field, direction: f.assending ? 'Ascending' : 'Descending' };
        if (!columns) {
          columns = [column];
        }
        else {
          columns.push(column);
        }
      });
      this.grid.sortSettings.columns = columns;
    }
  }

  // Group by
  private columnGroupByFieldName(fieldName: string) {
    if (fieldName) {
      var columns: string[] = this.grid.groupSettings.columns;
      if (!columns) {
        columns = [fieldName];
      }
      else {
        columns.push(fieldName);
      }
      this.grid.groupSettings.columns = columns;
    }
  }

  public columnsGroupBySetup(args: any) {

    var groupByColumnCount: number = this.grid.groupSettings.columns.length;
    if (groupByColumnCount != this.groupByColumnCount) {
      this.groupByColumnCount = groupByColumnCount;

      if (groupByColumnCount > 0) {
        this.grid.allowPaging = false;
        //this.grid.groupModule?ollapseAll();
        this.grid.groupSettings.showDropArea = true;
      }
      else {
        this.grid.allowPaging = true;
        this.grid.groupSettings.showDropArea = false;
      }
    }
  }

  // Snackbar
  public showMessage(msg: string, action: string = null, duration: number = null) {
    this.snackBar.open(msg, action, { duration: 1000 });
  }
}


